import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText, Rocket, Bike, GaugeCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine Protection
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaFileInvoice,      // For Return to Invoice
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaUserShield,
    FaHardHat,         // For Helmet and Riding Gear
} from "react-icons/fa";
import { GiScooter } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const KTMBikeInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is insurance mandatory for my KTM Duke or RC bike?",
      answer: "Yes, at least third-party insurance is legally required in India.",
    },
    {
      question: "What type of insurance should I get for a KTM 390?",
      answer: "A comprehensive policy with zero depreciation and engine protection is highly recommended.",
    },
    {
      question: "How much does insurance cost for KTM bikes?",
      answer: "Starting premiums range between ₹1,600 – ₹3,200 depending on the model and coverage.",
    },
    {
      question: "Is engine protection useful for KTM owners?",
      answer: "Yes, it’s valuable for performance bikes often exposed to heavy rains or long rides.",
    },
    {
      question: "Can I insure my aftermarket accessories?",
      answer: "Yes, declare their value while purchasing insurance to cover them during claims.",
    },
    {
      question: "What is NCB in KTM insurance?",
      answer: "No Claim Bonus is a discount for not claiming insurance in the previous year.",
    },
    {
      question: "Can I switch insurers during renewal?",
      answer: "Absolutely. Use platforms like TheFinMaster to compare and switch easily.",
    },
    {
      question: "Does my insurance cover track racing or off-roading?",
      answer: "Standard policies do not. Special endorsements or exclusions may apply.",
    },
    {
      question: "Is KTM Adventure 390 treated differently for insurance?",
      answer: "Only in terms of higher IDV and possible need for wider coverage like trip assistance.",
    },
    {
      question: "Will Zero Depreciation increase my premium?",
      answer: "Yes slightly, but it results in higher claim payouts without deductions.",
    },
    {
      question: "Do I need to renew insurance every year?",
      answer: "Yes, unless you’ve opted for a long-term policy of 2 or 3 years.",
    },
    {
      question: "Does KTM offer in-house insurance options?",
      answer: "Yes, but it’s advisable to compare with external insurers for broader coverage and pricing.",
    },
    {
      question: "Can I get cashless repairs for KTM bikes?",
      answer: "Yes, with insurers that have tie-ups with KTM-authorized service centers.",
    },
    {
      question: "What happens if I ride without valid insurance?",
      answer: "You may face legal penalties, fines, and be denied claims during accidents.",
    },
    {
      question: "Does insurance cover theft of KTM bikes?",
      answer: "Yes, comprehensive insurance covers theft after FIR and verification process.",
    },
    {
      question: "Can I buy KTM insurance online?",
      answer: "Yes, online policies are quicker, cheaper, and paperless.",
    },
    {
      question: "What to do after an accident with a KTM bike?",
      answer: "Inform the insurer, take photos, file an FIR (if needed), and begin the claim process.",
    },
    {
      question: "Will minor scratches or dents be covered?",
      answer: "Yes, if they’re part of accidental damage and not considered wear & tear.",
    },
    {
      question: "Do performance parts affect the insurance rate?",
      answer: "They might. Always disclose modifications or upgrades when buying the policy.",
    },
    {
      question: "Can I insure a used KTM bike?",
      answer: "Yes, even second-hand KTM bikes can be insured or renewed.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Ensures full claim amount for parts replacement.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine (and Gearbox) Protection",
      description: "Covers internal damages due to water ingress or oil leaks.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Towing, flat battery, on-spot repair, and emergency fuel.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Return to Invoice Cover",
      description: "Full invoice value refund in case of total loss or theft.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers costs of oil, nuts, bolts, brake fluid, etc. during repair.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Helmet and Riding Gear Cover",
      description: "Insures high-value protective gear and helmet.",
      icon: <FaHardHat className="text-4xl text-orange-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];

  const benefits = [
    {
      title: "KTM Duke Series",
      desc: "Naked street bikes with punchy performance (125, 200, 250, 390).",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "KTM RC Series",
      desc: "Full-faired sports bikes designed for the track (125, 200, 390).",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "KTM Adventure Series",
      desc: "Dual-sport bikes built for long rides and trails (250, 390).",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
  ];
const ktminsurance = [
    {
      name: "125 Duke",
      path: "/ktm-125duke-bike-insurance", 
      image: "/assets/ktmduke125.jpeg",
    },
  {
      name: "200 Duke",
      path: "/ktm-200duke-bike-insurance", 
      image: "/assets/ktmduke200.jpeg",
    },
     {
      name: "250 Duke",
      path: "/ktm-250duke-bike-insurance", 
      image: "/assets/ktmduke250.jpeg",
    },
     {
      name: "390 Duke",
      path: "/ktm-390duke-bike-insurance", 
      image: "/assets/ktmduke390.jpeg",
    },
    {
      name: "RC 125",
      path: "/ktm-rc125-bike-insurance", 
      image: "/assets/ktmrc125.jpeg",
    },
    {
      name: "RC 200",
      path: "/ktm-rc200-bike-insurance", 
      image: "/assets/ktmrc200.jpeg",
    },
     {
      name: "RC 390",
      path: "/ktm-rc390-bike-insurance", 
      image: "/assets/ktmrc390.jpeg",
    },
      {
      name: "Adventure 390",
      path: "/ktm-adventure390-bike-insurance", 
      image: "/assets/ktmadventure390.jpeg",
    },
  ];

  return (
    <>
      <Helmet>
        <title>KTM Bike Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore KTM bike insurance options in India. Compare quotes, customize coverage, and buy online to protect your KTM Duke, RC, or Adventure series."
        />
        <meta
          name="keywords"
          content="ktm bike insurance, ktm duke insurance, ktm rc insurance, ktm adventure insurance, two-wheeler insurance quotes"
        />
         <link rel="canonical" href="https://thefinmaster.com/ktm-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/ktm-bike-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="KTM Bike Insurance in India" />
        <meta property="og:description" content="Compare and buy KTM bike insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/ktm-bike-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">KTM Bike Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">KTM Bike Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                KTM is known for its aggressive styling, powerful performance, and thrilling riding experience. Whether you ride a sporty Duke or a high-performance RC, safeguarding your bike starts with the right insurance plan. This guide takes you through everything you need to know about insuring a KTM bike, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/ktmissurance.jpeg"
                alt="KTM Bike"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About KTM Bikes in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            KTM (Kronreif & Trunkenpolz Mattighofen) is an Austrian motorcycle brand that entered India through a partnership with Bajaj Auto. Known for lightweight, high-performance bikes, KTM has become a top choice among Indian youth and biking enthusiasts.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
         <div className="p-4 sm:p-8 bg-white">
                          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                            Choose Your Bike Brand
                          </h2>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {ktminsurance.map((calc, index) => (
                              <motion.div
                                key={index}
                                whileHover={{ scale: 1.04 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                              >
                                <Link
                                  to={calc.path}
                                  className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                >
                                  <img
                                    src={calc.image}
                                    alt={calc.name}
                                    className="w-16 h-16 object-contain mb-2"
                                  />
                                  <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                    {calc.name}
                                  </span>
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>


      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why KTM Owners Need Comprehensive Bike Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Owning a KTM is exciting, but high-performance bikes also come with increased risk on Indian roads. Here's why insurance is non-negotiable:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Required by law to at least have third-party coverage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Protects against accident damage, fire, theft, and natural disasters</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Covers liabilities to third parties</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Supports expensive repair costs with cashless claims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Peace of mind for adventurous riders and commuters alike</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your KTM stays protected, legally compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
       <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10">
            <div className="max-w-xl space-y-6 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                Calculate Your TwoWheeler Insurance <br />
                in <span className="text-blue-600">Seconds</span>
              </h2>
              <p className="text-gray-700 text-lg">
                Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
              </p>
              <Link
                to="/financial-tools-calculators/bike-insurance-calculator"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
              >
                Start Calculating <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="space-y-4 text-left">
              {features.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                  <span className="text-gray-800 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          KTM Bike Models & Insurance Premium Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Premiums depend on location, IDV selected, rider profile, and chosen add-ons.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "KTM Duke 125", category: "Entry-level Naked", idv: "₹1.40 – ₹1.50 Lakh", premium: "₹1,600 – ₹1,800" },
              { model: "KTM Duke 200", category: "Performance Naked", idv: "₹1.80 – ₹2.00 Lakh", premium: "₹1,800 – ₹2,200" },
              { model: "KTM Duke 250", category: "Mid-segment Naked", idv: "₹2.30 – ₹2.50 Lakh", premium: "₹2,000 – ₹2,500" },
              { model: "KTM Duke 390", category: "Premium Naked", idv: "₹3.10 – ₹3.40 Lakh", premium: "₹2,400 – ₹2,800" },
              { model: "KTM RC 200", category: "Faired Sports Bike", idv: "₹2.10 – ₹2.30 Lakh", premium: "₹2,000 – ₹2,400" },
              { model: "KTM RC 390", category: "Track Performance", idv: "₹3.30 – ₹3.50 Lakh", premium: "₹2,500 – ₹3,000" },
              { model: "KTM 390 Adventure", category: "ADV Bike", idv: "₹3.30 – ₹3.50 Lakh", premium: "₹2,500 – ₹3,200" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">Segment</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["KTM Duke 125", "Entry-level Naked", "₹1.40 – ₹1.50 Lakh", "₹1,600 – ₹1,800"],
                  ["KTM Duke 200", "Performance Naked", "₹1.80 – ₹2.00 Lakh", "₹1,800 – ₹2,200"],
                  ["KTM Duke 250", "Mid-segment Naked", "₹2.30 – ₹2.50 Lakh", "₹2,000 – ₹2,500"],
                  ["KTM Duke 390", "Premium Naked", "₹3.10 – ₹3.40 Lakh", "₹2,400 – ₹2,800"],
                  ["KTM RC 200", "Faired Sports Bike", "₹2.10 – ₹2.30 Lakh", "₹2,000 – ₹2,400"],
                  ["KTM RC 390", "Track Performance", "₹3.30 – ₹3.50 Lakh", "₹2,500 – ₹3,000"],
                  ["KTM 390 Adventure", "ADV Bike", "₹3.30 – ₹3.50 Lakh", "₹2,500 – ₹3,200"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for KTM Bike Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Given KTM’s performance focus and high-speed potential, the right add-ons offer enhanced protection:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurance Providers for KTM Bikes in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Choose from trusted insurers with KTM-compatible garage networks and hassle-free digital claims:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Trusted insurer with KTM-compatible garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Known for wide garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Hassle-free digital claims process.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">TATA AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Offers smart policy options for KTM bikes.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">Seamless app experience and cashless claims.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🛡️</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Reliance General Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Reliable coverage with digital claims support.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required to Insure a KTM Bike
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew your insurance, you’ll need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bike registration certificate (RC)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof (Aadhaar, PAN, etc.)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance copy (if renewing)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Invoice (for new KTM bikes)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-size photo</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PUC certificate (if required)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About KTM Bike Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <GiScooter />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                KTM bikes deliver an exhilarating ride, but with great power comes the responsibility of protection. Whether you own a Duke, RC, or Adventure series model, choosing the right insurance ensures you're prepared for any unexpected mishap.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we simplify the insurance journey for bikers. Compare real-time quotes, discover value-packed add-ons, and secure your KTM’s future in just a few clicks.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default KTMBikeInsurance;